/**
 * La cave › Stock (/cave/stock) — tableau groupé par cuvée, filtres, recherche,
 * dépliage des références. Le prix d'achat est masqué en mode écran public.
 */
import { useState, useMemo, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ChevronRight, ChevronDown, FileDown, ClipboardList, MoreHorizontal } from 'lucide-react';
import { caveApi } from '../../services/api';
import { usePublicMode } from '../../context/PublicModeContext';
import { formatInt, formatEuro } from '../../utils/format';
import { SegmentedControl, Spinner, EmptyState } from '../../components/ui/kit';
import { WineColorDot, Hearts, StatusTag, MaskedValue } from '../../components/wine/wine';
import { COLOR_FILTER_ORDER, WINE_COLOR_DOTS, STOCK_GROUPINGS } from '../../constants';

const FILTERS = [
  { value: '', label: 'Tout' },
  { value: 'a_boire', label: 'À boire maintenant' },
  { value: 'stock_faible', label: 'Stock faible' },
  { value: 'recents', label: 'Récents' },
  { value: 'degustes', label: 'Dégustés' },
  { value: 'archives', label: 'Archivées' },
];

/** Affiche une fourchette de prix (ou une valeur unique). */
function range(min, max, opts) {
  if (min == null && max == null) return '—';
  if (min === max) return formatEuro(min, opts);
  return `${formatEuro(min, opts)} – ${formatEuro(max, opts)}`;
}

export default function Stock() {
  const navigate = useNavigate();
  const { isPublic } = usePublicMode();
  const [filter, setFilter] = useState('');
  const [colors, setColors] = useState([]);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState({});
  const [view, setView] = useState(() => localStorage.getItem('vd-stock-view') || 'cuvee');
  const [closedGroups, setClosedGroups] = useState({});
  useEffect(() => { localStorage.setItem('vd-stock-view', view); }, [view]);

  const isDegustesView = filter === 'degustes';
  const mapDeguste = (list) => (list || [])
    .filter((c) => !colors.length || colors.includes(c.color))
    .map((c) => ({
      ...c, status: 'deguste', references: [], years: [], locations: [], total_stock: 0,
      vintages_count: 0, formats_count: 0, purchase_min: null, purchase_max: null,
      price_table_min: null, price_table_max: null,
    }));
  const { data, isLoading } = useQuery({
    queryKey: ['stock', filter, colors, q],
    queryFn: async () => {
      if (isDegustesView) {
        const rows = mapDeguste(await caveApi.degustes({ q }));
        return { rows, total_cuvees: rows.length, total_references: 0, total_bottles: 0, degustes: true };
      }
      const base = await caveApi.stock({ filter, color: colors.join(','), q });
      // « Tout » inclut aussi les vins dégustés (tous les vins présents/référencés dans la cave).
      if (filter === '') {
        const deg = mapDeguste(await caveApi.degustes({ q }));
        base.rows = [...(base.rows || []), ...deg];
        base.total_cuvees = (base.total_cuvees || 0) + deg.length;
      }
      return base;
    },
    placeholderData: keepPreviousData,
  });

  const toggleColor = (c) => setColors((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const rows = data?.rows || [];

  // Regroupement selon le mode d'affichage (§4) : par cuvée (à plat), par vignoble (région), par emplacement.
  const groups = useMemo(() => {
    if (view === 'cuvee' || isDegustesView) return [{ key: '_all', label: null, rows }];
    const keyOf = view === 'vignoble'
      ? (r) => r.region || 'Autres régions'
      : (r) => (r.locations?.[0] || '—').split('·')[0].trim() || '—';
    const map = {};
    rows.forEach((r) => { const k = keyOf(r); (map[k] || (map[k] = [])).push(r); });
    return Object.keys(map).sort((a, b) => a.localeCompare(b)).map((k) => {
      const grp = map[k].sort((a, b) => (a.domain_name || '').localeCompare(b.domain_name || '') || a.name.localeCompare(b.name));
      return {
        key: k, label: k, rows: grp,
        refs: grp.reduce((s, r) => s + (r.references?.length || 0), 0),
        bottles: grp.reduce((s, r) => s + (r.total_stock || 0), 0),
      };
    });
  }, [rows, view]);

  const renderDegusteRow = (row) => (
    <tr key={row.id} data-testid={`stock-row-${row.id}`} className="hair-t hover:bg-surface/60 cursor-pointer align-top"
      onClick={() => navigate(`/cave/cuvee/${row.id}`)}>
      <td className="pl-2 py-3"></td>
      <td className="py-3 px-2">
        <div className="flex items-center gap-2">
          <WineColorDot color={row.color} />
          <span className="font-title text-lg leading-tight">{row.name}</span>
          {row.hearts > 0 && <Hearts count={row.hearts} size={12} />}
        </div>
        <span className="block text-[12px] text-n500 ml-4">{row.domain_name}</span>
      </td>
      <td className="py-3 px-2 hidden md:table-cell text-n700">{row.appellation}</td>
      <td className="py-3 px-2 hidden lg:table-cell text-n600">{[row.place, row.tasting_date].filter(Boolean).join(' · ') || '—'}</td>
      <td className="py-3 px-2 hidden lg:table-cell"></td>
      <td className="py-3 px-2 text-right nums text-n500">—</td>
      <td className="py-3 px-2 text-right nums text-n500 hidden md:table-cell">—</td>
      <td className="py-3 px-2 text-right nums text-n500 hidden sm:table-cell">—</td>
      <td className="py-3 px-2"><StatusTag status="deguste" /></td>
    </tr>
  );

  const renderRow = (row) => {
    if (row.status === 'deguste') return renderDegusteRow(row);
    const multi = row.references.length > 1;
    const isOpen = expanded[row.id];
    return (
      <Fragment key={row.id}>
        <tr data-testid={`stock-row-${row.id}`} className="hair-t hover:bg-surface/60 cursor-pointer align-top"
          onClick={() => navigate(`/cave/cuvee/${row.id}`)}>
          <td className="pl-2 py-3">
            {multi && (
              <button data-testid={`expand-${row.id}`} onClick={(e) => { e.stopPropagation(); toggleExpand(row.id); }} className="text-n500 hover:text-ink">
                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
          </td>
          <td className="py-3 px-2">
            <div className="flex items-center gap-2">
              <WineColorDot color={row.color} />
              <span className="font-title text-lg leading-tight">{row.name}</span>
              {row.hearts > 0 && <Hearts count={row.hearts} size={12} />}
            </div>
            <span className="block text-[12px] text-n500 ml-4">{row.domain_name}</span>
          </td>
          <td className="py-3 px-2 hidden md:table-cell text-n700">{row.appellation}</td>
          <td className="py-3 px-2 hidden lg:table-cell text-n700 nums">
            {row.vintages_count > 1 || row.formats_count > 1
              ? `${row.vintages_count} mill. · ${row.formats_count} format${row.formats_count > 1 ? 's' : ''}`
              : `${row.years[0]} · ${row.references[0]?.format_label}`}
          </td>
          <td className="py-3 px-2 hidden lg:table-cell text-n600 nums">{row.locations.slice(0, 2).join(', ')}</td>
          <td className="py-3 px-2 text-right nums">{formatInt(row.total_stock)}</td>
          <td className="py-3 px-2 text-right nums text-n700 hidden md:table-cell">
            <MaskedValue masked={isPublic || row.financials_hidden}>{range(row.purchase_min, row.purchase_max)}</MaskedValue>
          </td>
          <td className="py-3 px-2 text-right nums hidden sm:table-cell">{range(row.price_table_min, row.price_table_max)}</td>
          <td className="py-3 px-2"><StatusTag status={row.status} /></td>
        </tr>
        {isOpen && row.references.map((ref) => (
          <tr key={ref.id} className="bg-surface/40 text-[12.5px]" data-testid={`ref-row-${ref.id}`}>
            <td></td>
            <td className="py-1.5 px-2 pl-6 nums text-n700">{ref.year} · {ref.format_label}</td>
            <td className="hidden md:table-cell"></td>
            <td className="hidden lg:table-cell"></td>
            <td className="py-1.5 px-2 hidden lg:table-cell text-n600 nums">{ref.location_code}</td>
            <td className="py-1.5 px-2 text-right nums">{formatInt(ref.quantity)}</td>
            <td className="py-1.5 px-2 text-right nums text-n700 hidden md:table-cell">
              <MaskedValue masked={isPublic || ref.financials_hidden}>{formatEuro(ref.purchase_price_ht)}</MaskedValue>
            </td>
            <td className="py-1.5 px-2 text-right nums hidden sm:table-cell">{formatEuro(ref.price_table)}</td>
            <td></td>
          </tr>
        ))}
      </Fragment>
    );
  };

  return (
    <div className="p-4 md:p-6">
      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SegmentedControl options={FILTERS} value={filter} onChange={setFilter} testId="stock-filter" />
        <SegmentedControl options={STOCK_GROUPINGS} value={view} onChange={setView} testId="stock-view" />
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="color-filters">
          {COLOR_FILTER_ORDER.map((c) => {
            const active = colors.includes(c);
            return (
              <button key={c} data-testid={`color-filter-${c}`} onClick={() => toggleColor(c)}
                className={`inline-flex items-center gap-1.5 px-2.5 h-8 rounded-full border text-[12.5px] transition-colors ${active ? 'vd-tag-fill border-transparent' : 'border-hair text-n600 hover:bg-surface'}`}>
                <WineColorDot color={c} size={10} /> {WINE_COLOR_DOTS[c].label}
              </button>
            );
          })}
        </div>
        <input data-testid="stock-search" className="vd-input h-8 max-w-[220px] ml-auto" placeholder="Recherche instantanée…"
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {/* Récapitulatif (au-dessus du tableau) + menu d'actions */}
      {data && (
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-[13px] text-n600 nums" data-testid="stock-summary">
            {isDegustesView
              ? `${data.total_cuvees} vin(s) dégusté(s)`
              : `${data.total_cuvees} cuvées · ${data.total_references} références · ${formatInt(data.total_bottles)} bouteilles`}
          </p>
          <StockActionsMenu />
        </div>
      )}

      {/* Tableau */}
      {isLoading && !data ? (
        <Spinner label="Chargement de la cave…" />
      ) : rows.length === 0 ? (
        <EmptyState title="Aucune cuvée" description="Ajustez les filtres ou la recherche." testId="stock-empty" />
      ) : (
        <div className="vd-card overflow-hidden">
          <table className="w-full text-[13.5px]" data-testid="wine-stock-table">
            <thead>
              <tr className="text-left kicker">
                <th className="w-8"></th>
                <th className="py-2.5 px-2 font-medium">Cuvée</th>
                <th className="py-2.5 px-2 font-medium hidden md:table-cell">Appellation</th>
                <th className="py-2.5 px-2 font-medium hidden lg:table-cell">Millésimes · formats</th>
                <th className="py-2.5 px-2 font-medium hidden lg:table-cell">Emplacement</th>
                <th className="py-2.5 px-2 font-medium text-right">Stock</th>
                <th className="py-2.5 px-2 font-medium text-right hidden md:table-cell">Achat</th>
                <th className="py-2.5 px-2 font-medium text-right hidden sm:table-cell">Prix table</th>
                <th className="py-2.5 px-2 font-medium">Statut</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const gClosed = closedGroups[g.key];
                return (
                  <Fragment key={g.key}>
                    {g.label && (
                      <tr className="bg-surface/70 sticky top-0" data-testid={`stock-group-${g.key}`}>
                        <td colSpan={9} className="py-2 px-2">
                          <button className="flex items-center gap-2 w-full text-left"
                            onClick={() => setClosedGroups((p) => ({ ...p, [g.key]: !p[g.key] }))}
                            data-testid={`group-toggle-${g.key}`}>
                            {gClosed ? <ChevronRight className="w-4 h-4 text-n500" /> : <ChevronDown className="w-4 h-4 text-n500" />}
                            <span className="font-title text-lg">{g.label}</span>
                            <span className="text-[12px] text-n500 nums">· {g.refs} réf. · {formatInt(g.bottles)} btl</span>
                          </button>
                        </td>
                      </tr>
                    )}
                    {!gClosed && g.rows.map(renderRow)}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Menu « ⋯ » du listing (actions encore en développement → grisées). */
function StockActionsMenu() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" data-testid="stock-actions">
      <button data-testid="stock-actions-btn" onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 inline-flex items-center justify-center rounded-md border border-hair hover:bg-surface text-n700"
        aria-label="Actions du listing">
        <MoreHorizontal className="w-5 h-5" strokeWidth={1.6} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-50 w-64 bg-paper border border-hair rounded-md shadow-md py-1" data-testid="stock-actions-menu">
            <button disabled title="Bientôt disponible" data-testid="export-stock-pdf"
              className="flex items-center gap-2 w-full text-left px-3 py-2 text-[13px] text-n400 cursor-not-allowed opacity-60">
              <FileDown className="w-4 h-4" strokeWidth={1.6} /> Exporter l'état du stock (PDF)
            </button>
            <button disabled title="Bientôt disponible" data-testid="inventory-guided"
              className="flex items-center gap-2 w-full text-left px-3 py-2 text-[13px] text-n400 cursor-not-allowed opacity-60">
              <ClipboardList className="w-4 h-4" strokeWidth={1.6} /> Inventaire guidé
            </button>
          </div>
        </>
      )}
    </div>
  );
}
